import { useState } from "react";
import { useParams, useNavigate, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { BirthdayPicker, Button, Input } from "@jourfx/ui";
import { ClientLayout } from "../components/layout/ClientLayout";
import { useSession } from "../hooks/useSession";

export function AssessmentPage() {
  const { appointmentId } = useParams({ from: "/assessment/$appointmentId" });
  const navigate = useNavigate();
  const { isValid, isLoading, session } = useSession(appointmentId);
  const { t } = useTranslation("assessment");

  const createAssessment = useMutation(api.assessments.index.createByVersicherter);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    placeOfResidence: "",
    insuranceNumber: "",
    knownDiagnoses: "",
    currentMedications: "",
    allergies: "",
    reasonForAssessment: "",
  });

  if (!isLoading && !isValid && appointmentId && !import.meta.env.DEV) {
    return <Navigate to="/" search={{ id: appointmentId }} />;
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!appointmentId || !session?.sessionToken) return;

    await createAssessment({
      appointmentId: appointmentId as any,
      sessionToken: session.sessionToken,
      name: `${form.firstName} ${form.lastName}`.trim(),
      dateOfBirth: form.dateOfBirth,
      placeOfResidence: form.placeOfResidence,
      insuranceNumber: form.insuranceNumber || undefined,
      knownDiagnoses: JSON.stringify(
        form.knownDiagnoses ? [{ icdCode: "", description: form.knownDiagnoses }] : []
      ),
      currentMedications: JSON.stringify(
        form.currentMedications ? [{ name: form.currentMedications, dosage: "", frequency: "" }] : []
      ),
      allergies: JSON.stringify(
        form.allergies ? [{ substance: form.allergies, reactionType: "" }] : []
      ),
      reasonForAssessment: form.reasonForAssessment,
    });

    navigate({ to: "/tech-check/$appointmentId", params: { appointmentId } });
  };

  return (
    <ClientLayout>
      <form onSubmit={onSubmit} className="client-card w-full max-w-lg p-6 space-y-4">
        <h1 className="text-2xl font-bold text-[var(--stone-800)]">{t("title")}</h1>

        <label className="block text-sm font-medium text-[var(--stone-700)]">
          {t("form.firstName.label")}
          <Input
            value={form.firstName}
            onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
          />
        </label>

        <label className="block text-sm font-medium text-[var(--stone-700)]">
          {t("form.lastName.label")}
          <Input
            value={form.lastName}
            onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
          />
        </label>

        <div className="block text-sm font-medium text-[var(--stone-700)]">
          {t("form.dateOfBirth.label")}
          <BirthdayPicker
            value={form.dateOfBirth}
            onChange={(date) => setForm((prev) => ({ ...prev, dateOfBirth: date }))}
            className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <label className="block text-sm font-medium text-[var(--stone-700)]">
          {t("form.placeOfResidence.label")}
          <Input
            value={form.placeOfResidence}
            onChange={(e) => setForm((prev) => ({ ...prev, placeOfResidence: e.target.value }))}
          />
        </label>

        <label className="block text-sm font-medium text-[var(--stone-700)]">
          {t("form.reason.label")}
          <Input
            value={form.reasonForAssessment}
            onChange={(e) => setForm((prev) => ({ ...prev, reasonForAssessment: e.target.value }))}
          />
        </label>

        <Button type="submit">{t("submit")}</Button>
      </form>
    </ClientLayout>
  );
}
